<template>
  <table class="table is-bordered is-striped is-fullwidth">
    <tbody>
      <tr>
        <td colspan="8" class="has-text-right is-borderless">
          Καθαρή Αξία
        </td>
        <td class="has-text-centered">
          <strong>{{ netPrice }} &euro;</strong>
        </td>
      </tr>
      <tax
        v-for="(tax, index) in Object.keys(taxes)"
        :key="index"
        :tax="tax"
        :taxes="taxes"
      />
      <tr>
        <td colspan="8" class="has-text-right is-borderless">
          Σύνολο Προϊόντων
        </td>
        <td class="has-text-centered">
          <strong>{{ price }} &euro;</strong>
        </td>
      </tr>
      <tr v-if="showLoyaltyDiscount">
        <td colspan="8" class="has-text-right is-borderless">
          Έκπτωση Care-δίζω ({{ displayLoyaltyPercentage }}%)
        </td>
        <td class="has-text-centered">
          <strong>&minus;{{ displayLoyaltyAmount }} &euro;</strong>
        </td>
      </tr>
      <tr v-if="hasCoupon">
        <td colspan="8" class="has-text-right is-borderless">
          Κουπόνι "{{ order.coupon.code }}"
        </td>
        <td class="has-text-centered">
          <strong v-if="order.coupon.type === 'percentage'">
            &minus; {{ order.coupon.value }}% ({{ couponDiscountAmount }} €)
          </strong>
          <strong v-else>&minus;{{ order.coupon.value }} &euro;</strong>
        </td>
      </tr>
      <tr v-if="order.checkout_charge">
        <td colspan="8" class="has-text-right is-borderless">
          Επιβάρυνση Τρόπου Πληρωμής
        </td>
        <td class="has-text-centered">
          <strong>{{ order.checkout_charge }} &euro;</strong>
        </td>
      </tr>
      <tr v-if="hasSendCharge">
        <td colspan="8" class="has-text-right is-borderless">
          Έξοδα Αποστολής
          <span v-if="hasSendChargeChanged" class="has-text-grey is-size-7">
            (αρχικά: {{ originalSendCharge }} €)
          </span>
        </td>
        <td class="has-text-centered">
          <strong>{{ displaySendCharge }} &euro;</strong>
          <span
            v-if="hasSendChargeChanged"
            class="tag is-warning is-light ml-1"
          >
            νέο
          </span>
        </td>
      </tr>
      <tr v-if="order.is_gift">
        <td colspan="8" class="has-text-right is-borderless">
          Συσκευασία Δώρου
        </td>
        <td class="has-text-centered">
          <strong>{{ order.gift_cost }} &euro;</strong>
        </td>
      </tr>
      <tr>
        <td colspan="8" class="has-text-right is-borderless title is-5">
          Συνολικό Ποσό
        </td>
        <td class="has-text-centered title is-5">
          <strong>{{ finalPrice }} &euro;</strong>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
import { get, round } from 'lodash';
import Tax from './components/Tax';

export default {
  name: 'Logistics',

  components: {
    Tax,
  },

  props: {
    order: {
      type: Object,
      required: true,
    },

    hasLoyalty: {
      type: Boolean,
      required: false,
      default: false,
    },

    loyalty: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },

    /**
     * When true, always use calculated value (for Add order or Edit mode preview).
     * When false, prefer sum_total from API for existing orders.
     */
    isPreview: {
      type: Boolean,
      required: false,
      default: false,
    },

    /**
     * Original send_charge from the database (before any edits).
     * Used to detect if shipping cost has changed during editing.
     */
    originalSendCharge: {
      type: Number,
      required: false,
      default: null,
    },
  },

  computed: {
    productsPerTax() {
      return this.order.products.reduce((acc, item) => {
        const price = item.pivot.final_price * item.pivot.count;
        const { percentage } = item.tax;

        if (percentage in acc) {
          acc[percentage] = round(acc[percentage] + price, 2);
        } else {
          acc[percentage] = price;
        }

        return acc;
      }, {});
    },

    taxes() {
      return Object.keys(this.productsPerTax).reduce((acc, item) => {
        const taxAmount =
          (this.productsPerTax[item] / (1 + item / 100)) * (item / 100);
        return {
          ...acc,
          [item]: round(taxAmount, 2),
        };
      }, {});
    },

    totalTax() {
      const { taxes } = this;

      return Object.keys(taxes).reduce(
        (acc, key) => round(acc + taxes[key], 2),
        0,
      );
    },

    hasCoupon() {
      return !!this.order.coupon;
    },

    hasSendCharge() {
      const country = get(this.order, 'charge_address.country', '');
      return country !== '';
    },

    /**
     * Check if send_charge has changed from the original stored value.
     * Shows indicator when ANY edit (products, shipping method, etc.) changes the cost.
     * Using == null to catch both null and undefined.
     */
    hasSendChargeChanged() {
      if (this.originalSendCharge == null) {
        return false;
      }
      return this.order.send_charge !== this.originalSendCharge;
    },

    /**
     * Send charge to display.
     * In view mode: Use stored value from order.
     * In edit mode: Use current (possibly recalculated) value.
     */
    displaySendCharge() {
      return this.order.send_charge;
    },

    price() {
      return round(
        this.order.products.reduce(
          (acc, item) => acc + item.pivot.final_price * item.pivot.count,
          0,
        ),
        2,
      );
    },

    netPrice() {
      return round(this.price - this.totalTax, 2);
    },

    /**
     * Check if loyalty discount should be shown.
     * In preview mode, show if customer has loyalty program OR if order has stored discount.
     * In view mode, show if order has stored loyalty discount.
     * This ensures the display matches what's included in calculatedTotalPrice.
     */
    showLoyaltyDiscount() {
      if (this.isPreview) {
        // Show if customer currently has loyalty OR if order already had a loyalty discount
        return (
          (this.hasLoyalty && this.loyalty.discount > 0) ||
          this.hasLoyaltyDiscount
        );
      }
      return !!this.order.loyalty_discount_amount;
    },

    hasLoyaltyDiscount() {
      return !!this.order.loyalty_discount_amount;
    },

    loyaltyDiscountAtTheTimeOfOrder() {
      return round((this.order.loyalty_discount_amount * 100) / this.price);
    },

    /**
     * Calculated loyalty discount amount based on current products price.
     * Used in preview mode to show real-time calculation.
     */
    calculatedLoyaltyAmount() {
      if (this.hasLoyalty && this.loyalty.discount > 0) {
        return round((this.price * this.loyalty.discount) / 100, 2);
      }
      return 0;
    },

    /**
     * Loyalty percentage to display.
     * In preview mode with active loyalty, use the loyalty program percentage.
     * Otherwise, calculate from stored amount.
     */
    displayLoyaltyPercentage() {
      if (this.isPreview && this.hasLoyalty && this.loyalty.discount > 0) {
        return this.loyalty.discount;
      }
      // Fall back to stored percentage (calculated from stored amount)
      return this.loyaltyDiscountAtTheTimeOfOrder;
    },

    /**
     * Loyalty amount to display (in euros).
     * In preview mode with active loyalty, calculate based on current products.
     * Otherwise, use stored amount from the order.
     */
    displayLoyaltyAmount() {
      if (this.isPreview && this.hasLoyalty && this.loyalty.discount > 0) {
        return this.calculatedLoyaltyAmount;
      }
      // Fall back to stored amount (for orders where customer had loyalty at time of order)
      return this.order.loyalty_discount_amount;
    },

    /**
     * Coupon discount amount in euros (for percentage coupons).
     * Always calculated based on current products price.
     */
    couponDiscountAmount() {
      if (this.hasCoupon && this.order.coupon.type === 'percentage') {
        return round((this.price * this.order.coupon.value) / 100, 2);
      }
      return 0;
    },

    loyaltyDiscount() {
      if (this.hasLoyaltyDiscount) {
        return round((this.price * this.loyalty.discount) / 100, 2);
      }

      return 0;
    },

    /**
     * Calculate the total price for new orders (not yet saved).
     * Matches backend logic: both discounts are applied to the original price.
     */
    calculatedTotalPrice() {
      let itemsPrice = this.price;
      let totalDiscount = 0;

      // Calculate loyalty discount on original price
      // In preview mode, calculate from loyalty percentage; otherwise use stored amount
      if (this.isPreview && this.hasLoyalty && this.loyalty.discount > 0) {
        totalDiscount += this.calculatedLoyaltyAmount;
      } else if (this.hasLoyaltyDiscount) {
        totalDiscount += this.order.loyalty_discount_amount;
      }

      // Calculate coupon discount on original price (NOT on already-discounted price)
      if (this.hasCoupon) {
        totalDiscount +=
          this.couponDiscountAmount > 0
            ? this.couponDiscountAmount
            : this.order.coupon.value;
      }

      // Subtract total discount from original price
      itemsPrice = round(itemsPrice - totalDiscount, 2);
      itemsPrice = itemsPrice < 0 ? 0 : itemsPrice;

      /* eslint-disable camelcase */
      const { gift_cost, send_charge, checkout_charge } = this.order;

      return round(itemsPrice + gift_cost + send_charge + checkout_charge, 2);
    },

    /**
     * Final price to display.
     * Uses sum_total from API for existing orders (already correctly calculated by backend).
     * Falls back to calculated value for new orders, preview/edit mode, or when send_charge has changed.
     */
    finalPrice() {
      // In preview mode (Add order or Edit mode), always use calculated value
      if (this.isPreview) {
        return this.calculatedTotalPrice;
      }

      // If send_charge has changed (e.g. shipping method changed), recalculate total
      if (this.hasSendChargeChanged) {
        return this.calculatedTotalPrice;
      }

      // For existing orders in view mode, use the sum_total from the API (backend calculation is authoritative)
      if (this.order.sum_total !== undefined && this.order.sum_total !== null) {
        return this.order.sum_total;
      }

      // Fallback: calculate using the same logic as the backend
      return this.calculatedTotalPrice;
    },

    /**
     * @deprecated Use finalPrice instead. Kept for reference during migration.
     */
    totalPrice() {
      return this.calculatedTotalPrice;
    },
  },
};
</script>

<style scoped>
.table {
  margin-top: -1px;
}
</style>
